import type { ReactNode } from 'react';

import { useI18n } from '../i18n';

/**
 * Seitengerüst und Flächen.
 *
 * Jede Seite beginnt mit `PageLayout` und einem `PageHeader`. Dadurch haben
 * alle Bereiche dieselben Seitenränder, dieselbe Überschriftengröße und
 * denselben Abstand zwischen den Blöcken - auch auf großen Monitoren.
 */

export type Seitenbreite = 'breit' | 'mittel' | 'schmal';

export function PageLayout({
  width = 'breit',
  children,
}: {
  /** Formulare schmal, Listen und Tabellen breit. */
  width?: Seitenbreite;
  children?: ReactNode;
}): JSX.Element {
  const klasse = width === 'schmal' ? 'page page-schmal' : width === 'mittel' ? 'page page-mittel' : 'page';
  return <main className={klasse}>{children}</main>;
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string;
  description?: string;
  /** Die wichtigste Aktion der Seite, rechts oben. */
  actions?: ReactNode;
}): JSX.Element {
  return (
    <div className="page-header">
      <div className="page-header-text">
        <h1>{title}</h1>
        {description ? <p className="lead">{description}</p> : null}
      </div>
      {actions ? <div className="page-header-aktionen">{actions}</div> : null}
    </div>
  );
}

/** Ein abgegrenzter Block innerhalb einer Karte oder Seite. */
export function Section({
  title,
  description,
  actions,
  children,
}: {
  title?: string;
  description?: string;
  actions?: ReactNode;
  children: ReactNode;
}): JSX.Element {
  return (
    <section className="section">
      {title || actions ? (
        <div className="section-kopf">
          {title ? <h2>{title}</h2> : <span />}
          {actions ? <div className="row-actions">{actions}</div> : null}
        </div>
      ) : null}
      {description ? <p className="section-beschreibung">{description}</p> : null}
      {children}
    </section>
  );
}

export function Card({
  tone = 'normal',
  children,
}: {
  tone?: 'normal' | 'gefahr';
  children: ReactNode;
}): JSX.Element {
  return <div className={tone === 'gefahr' ? 'card card-danger' : 'card'}>{children}</div>;
}

/** Kartenraster, etwa für die Kennzahlen des Dashboards. */
export function CardGrid({ children }: { children: ReactNode }): JSX.Element {
  return <div className="raster">{children}</div>;
}

/**
 * Eine einzelne Kennzahl.
 *
 * Fehlt der Wert noch, steht hier bewusst nichts Erfundenes, sondern ein
 * Strich und der Hinweis, woher die Zahl später kommt.
 */
export function Metric({
  label,
  value,
  hint,
  help,
}: {
  label: string;
  value: string;
  hint?: string;
  help?: ReactNode;
}): JSX.Element {
  return (
    <div className="kennzahl">
      <span className="kennzahl-titel">
        {label}
        {help}
      </span>
      <span className="kennzahl-wert">{value}</span>
      {hint ? <span className="kennzahl-zusatz">{hint}</span> : null}
    </div>
  );
}

/** Zustand einer Liste, die (noch) nichts enthält. */
export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="leerzustand">
      <span className="leerzustand-titel">{title}</span>
      {description ? <p>{description}</p> : null}
      {action}
    </div>
  );
}

/** Während Daten geladen werden. */
export function LoadingState({ label }: { label?: string }): JSX.Element {
  const { t } = useI18n();
  return (
    <p className="lead" role="status">
      {label ?? t('app.loading')}
    </p>
  );
}

/** Wenn etwas schiefgegangen ist - immer mit Weg nach vorn. */
export function ErrorState({ message, action }: { message: string; action?: ReactNode }): JSX.Element {
  return (
    <div className="leerzustand">
      <p className="notice notice-error" role="alert">
        {message}
      </p>
      {action}
    </div>
  );
}

export function Tabs<T extends string>({
  tabs,
  active,
  onChange,
  label,
}: {
  tabs: { id: T; label: string }[];
  active: T;
  onChange: (id: T) => void;
  label: string;
}): JSX.Element {
  return (
    <nav className="tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === active}
          className={tab.id === active ? 'tab tab-active' : 'tab'}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </nav>
  );
}
